/**
 * API keys: machine access for trusted helpers (the boss's assistant pushing a
 * final PRP into Leads). A key is shown once, only its hash is kept, and it
 * only works on the /integration API with the rights ticked here.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Copy, KeyRound, Pencil, Plus } from 'lucide-react';
import { ApiError, api } from '../../lib/api';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  inputClass,
} from '../../components/ui';

interface ClientRow {
  id: string;
  name: string;
  keyPrefix: string;
  permissions: string[];
  status: 'ACTIVE' | 'REVOKED';
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  createdAt: string;
  defaultOwner: { id: string; name: string; email: string } | null;
  createdBy: string | null;
}

interface UserRow {
  id: string;
  name: string;
  email: string;
  status: 'ACTIVE' | 'DISABLED';
}

const RIGHTS: Record<string, string> = {
  'customers.view': 'See customers',
  'customers.create': 'Register customers',
  'customers.update': 'Fill in customer details and contacts',
  'leads.view_all': 'See leads',
  'leads.create': 'Create leads',
  'leads.update': 'Update leads and attach PRP / BOM files',
  'leads.view_commission': 'See deal commissions (BOM Section D)',
  'leads.manage_commission': 'Push deal commissions (BOM Section D; never on a confirmed lead)',
  'files.download_proposal': 'Download proposal files',
  'suppliers.view': 'See suppliers',
  'suppliers.manage': 'Register suppliers (never bank details)',
  'quotations.manage': 'Upload supplier quotations',
  'billing.view': 'See billings',
  'billing.create': 'Create manual billings (draft only; a person issues)',
  'leads.view_financials': 'Read financials (project cost, profit, margins)',
  'pricebook.view': 'Search the Price Book (supplier costs)',
  'pricebook.import': 'Import supplier price lists into the Price Book',
};

/** Off on a new key unless ticked on purpose. */
const OPT_IN = ['leads.view_financials', 'pricebook.view', 'pricebook.import'];
const DEFAULT_RIGHTS = Object.keys(RIGHTS).filter((key) => !OPT_IN.includes(key));

const when = (value: string | null): string =>
  value ? new Date(value).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' }) : 'Never';

export function ApiClientsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: '', defaultOwnerId: '', permissions: DEFAULT_RIGHTS });
  const [issued, setIssued] = useState<{ name: string; key: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ row: ClientRow; permissions: string[] } | null>(null);

  const clients = useQuery({
    queryKey: ['api-clients'],
    queryFn: () => api<{ data: ClientRow[]; allowedPermissions: string[] }>('/api-clients'),
  });
  const users = useQuery({
    queryKey: ['users', ''],
    queryFn: () => api<{ data: UserRow[] }>('/users?pageSize=100&q='),
    enabled: adding,
  });

  const close = (): void => {
    setAdding(false);
    setDraft({ name: '', defaultOwnerId: '', permissions: DEFAULT_RIGHTS });
    setError(null);
  };

  const create = useMutation({
    mutationFn: () =>
      api<{ client: { name: string }; key: string }>('/api-clients', {
        method: 'POST',
        body: {
          name: draft.name.trim(),
          permissions: draft.permissions,
          defaultOwnerId: draft.defaultOwnerId || null,
        },
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['api-clients'] });
      setIssued({ name: result.client.name, key: result.key });
      setCopied(false);
      close();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not create the key.'),
  });

  const saveRights = useMutation({
    mutationFn: (edit: { row: ClientRow; permissions: string[] }) =>
      api(`/api-clients/${edit.row.id}`, { method: 'PATCH', body: { permissions: edit.permissions } }),
    onSuccess: (_result, edit) => {
      void queryClient.invalidateQueries({ queryKey: ['api-clients'] });
      setEditing(null);
      setError(null);
      setNotice(`Rights for ${edit.row.name} saved. They apply to the next request.`);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not save the rights.'),
  });

  const revoke = useMutation({
    mutationFn: (row: ClientRow) => api(`/api-clients/${row.id}/revoke`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['api-clients'] });
      setNotice('Key revoked. It stops working immediately.');
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not revoke the key.'),
  });

  const rows = clients.data?.data ?? [];
  const allowed = clients.data?.allowedPermissions ?? Object.keys(RIGHTS);
  const flip = (list: string[], key: string): string[] =>
    list.includes(key) ? list.filter((p) => p !== key) : [...list, key];
  const rightsBoxes = (selected: string[], onToggle: (key: string) => void): JSX.Element => (
    <fieldset className="space-y-1.5">
      <legend className="mb-1 text-sm font-medium">Rights</legend>
      {allowed.map((key) => (
        <label key={key} className="flex min-h-[36px] items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={selected.includes(key)}
            onChange={() => onToggle(key)}
          />
          {RIGHTS[key] ?? key}
        </label>
      ))}
    </fieldset>
  );
  const toggleRight = (key: string): void =>
    setDraft({ ...draft, permissions: flip(draft.permissions, key) });

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-navy dark:text-white">API keys</h1>
          <p className="text-sm text-slate-500">
            Keys let a trusted helper push a final PRP and BOM into Leads and register customers. A
            key can never confirm a deal, bill, pay, approve or raise a PO, and everything it does is in the
            audit log under its own name.
          </p>
        </div>
        <Button onClick={() => setAdding(true)}>
          <Plus aria-hidden="true" className="h-4 w-4" /> New key
        </Button>
      </header>

      {notice && <Alert tone="ok">{notice}</Alert>}
      {error && !adding && !editing && <Alert tone="critical">{error}</Alert>}

      {clients.isLoading && <Skeleton className="h-48 w-full" />}

      {!clients.isLoading && rows.length === 0 && (
        <Card>
          <EmptyState title="No API keys yet" description="Create one for each helper, for example Anna." />
        </Card>
      )}

      {rows.length > 0 && (
        <Card>
          <TableWrap>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Key</Th>
                <Th>Leads default to</Th>
                <Th>Last used</Th>
                <Th>Status</Th>
                <Th>{''}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((row) => (
                <tr key={row.id}>
                  <Td>
                    <div className="font-medium">{row.name}</div>
                    <div className="text-xs text-slate-500">
                      {row.permissions.length} rights · by {row.createdBy ?? 'unknown'}
                    </div>
                  </Td>
                  <Td>
                    <span className="font-mono text-xs">sfk_{row.keyPrefix}_…</span>
                  </Td>
                  <Td>{row.defaultOwner?.name ?? '—'}</Td>
                  <Td>
                    <span className="text-xs">{when(row.lastUsedAt)}</span>
                    {row.lastUsedIp && <div className="text-xs text-slate-500">{row.lastUsedIp}</div>}
                  </Td>
                  <Td>
                    <StatusChip tone={row.status === 'ACTIVE' ? 'ok' : 'neutral'}>
                      {row.status === 'ACTIVE' ? 'Active' : 'Revoked'}
                    </StatusChip>
                  </Td>
                  <Td>
                    {row.status === 'ACTIVE' && (
                      <div className="flex">
                      <button
                        type="button"
                        className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                        aria-label={`Edit rights of ${row.name}`}
                        title="Edit rights"
                        onClick={() => {
                          setError(null);
                          setEditing({ row, permissions: [...row.permissions] });
                        }}
                      >
                        <Pencil aria-hidden="true" className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                        aria-label={`Revoke ${row.name}`}
                        onClick={() => {
                          if (window.confirm(`Revoke the key for ${row.name}? It stops working at once.`))
                            revoke.mutate(row);
                        }}
                      >
                        <Ban aria-hidden="true" className="h-4 w-4" />
                      </button>
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        </Card>
      )}

      {adding && (
        <Modal title="New API key" description="The key is shown once. Store it where the helper reads it." onClose={close}>
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              setError(null);
              create.mutate();
            }}
          >
            {error && <Alert tone="critical">{error}</Alert>}
            <Field label="Name" required hint="Who uses it, for example Anna">
              <input
                className={inputClass}
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                required
                autoFocus
              />
            </Field>
            <Field label="Leads default to" hint="Owner of a pushed lead when none is named">
              <select
                className={inputClass}
                value={draft.defaultOwnerId}
                onChange={(event) => setDraft({ ...draft, defaultOwnerId: event.target.value })}
              >
                <option value="">Nobody (every push must name an owner)</option>
                {users.data?.data
                  .filter((user) => user.status === 'ACTIVE')
                  .map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
              </select>
            </Field>
            {rightsBoxes(draft.permissions, toggleRight)}
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending || !draft.name.trim() || !draft.permissions.length}>
                <KeyRound aria-hidden="true" className="h-4 w-4" /> Create key
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {editing && (
        <Modal
          title={`Rights for ${editing.row.name}`}
          description="Changes apply to the key's next request and are recorded in the audit log."
          onClose={() => {
            setEditing(null);
            setError(null);
          }}
        >
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              setError(null);
              saveRights.mutate(editing);
            }}
          >
            {error && <Alert tone="critical">{error}</Alert>}
            {rightsBoxes(editing.permissions, (key) =>
              setEditing({ ...editing, permissions: flip(editing.permissions, key) }),
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saveRights.isPending || !editing.permissions.length}>
                Save rights
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {issued && (
        <Modal title={`Key for ${issued.name}`} description="Copy it now. It will not be shown again." onClose={() => setIssued(null)}>
          <div className="space-y-3">
            <code className="block break-all rounded-lg bg-slate-100 p-3 font-mono text-xs dark:bg-slate-800">
              {issued.key}
            </code>
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  void navigator.clipboard?.writeText(issued.key).then(() => setCopied(true));
                }}
              >
                <Copy aria-hidden="true" className="h-4 w-4" /> {copied ? 'Copied' : 'Copy'}
              </Button>
              <Button onClick={() => setIssued(null)}>Done</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
